/**
 * Microfrontend Loader
 * 
 * Dynamically loads and mounts microfrontend bundles.
 * Supports both module federation and standalone bundle loading.
 */

export interface LoadedMicrofrontend {
  component: React.ComponentType<any>;
  unmount?: () => void;
}

/**
 * Load a microfrontend bundle dynamically
 * 
 * @param bundleUrl - URL to the microfrontend bundle
 * @returns Promise resolving to the React component
 */
export async function loadMicrofrontend(
  bundleUrl: string
): Promise<LoadedMicrofrontend> {
  try {
    // Method 1: Try loading as ES module (for Vite-built apps)
    try {
      const module = await import(/* @vite-ignore */ bundleUrl);
      
      // Check if it's a default export component
      if (module.default && typeof module.default === "function") {
        return { component: module.default };
      }
      
      // Check if it's a named export
      if (module.App || module.default) {
        return { component: module.App || module.default };
      }
      
      throw new Error("No valid component found in module");
    } catch (esmError) {
      console.warn("ESM import failed, trying script tag method:", esmError);
      
      // Method 2: Load via script tag (for UMD bundles)
      return loadViaScriptTag(bundleUrl);
    }
  } catch (error) {
    console.error(`Failed to load microfrontend from ${bundleUrl}:`, error);
    throw new Error(`Failed to load microfrontend: ${error}`);
  }
}

/**
 * Load microfrontend via script tag (for UMD bundles)
 */
function loadViaScriptTag(bundleUrl: string): Promise<LoadedMicrofrontend> {
  return new Promise((resolve, reject) => {
    // Check if script already exists
    const existingScript = document.querySelector(
      `script[src="${bundleUrl}"]`
    );
    
    if (existingScript) {
      // Script already loaded, try to get the component from window
      const component = (window as any).__MICROFRONTEND_COMPONENT__;
      if (component) {
        return resolve({ component });
      }
    }

    const script = document.createElement("script");
    script.src = bundleUrl;
    script.type = "text/javascript";
    script.async = true;

    script.onload = () => {
      // Try to get component from window object
      const component = (window as any).__MICROFRONTEND_COMPONENT__;
      if (component) {
        resolve({ component });
      } else {
        reject(new Error("Component not found on window object after script load"));
      }
    };

    script.onerror = () => {
      reject(new Error(`Failed to load script from ${bundleUrl}`));
    };

    document.head.appendChild(script);
  });
}

/**
 * Load a module-federation remote module
 */
export async function loadRemoteModule(
  remoteEntry: string,
  remoteName: string,
  exposedModule: string
): Promise<React.ComponentType<any>> {
  const normalizedExposed = exposedModule.startsWith("./")
    ? `/${exposedModule.slice(2)}`
    : exposedModule.startsWith("/")
      ? exposedModule
      : `/${exposedModule}`;
  // 1. Load remoteEntry.js (only once)
  await new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector(
      `script[src="${remoteEntry}"]`
    ) as HTMLScriptElement | null;
    if (existingScript?.dataset.loaded === "true") {
      resolve();
      return;
    }

    const script = existingScript ?? document.createElement("script");
    script.src = remoteEntry;
    script.type = "module";
    script.dataset.loaded = "false";
    script.onload = () => {
      script.dataset.loaded = "true";
      resolve();
    };
    script.onerror = () => reject(new Error(`Failed to load ${remoteEntry}`));

    if (!existingScript) {
      document.head.appendChild(script);
    }
  });

  // 2. Import exposed module dynamically via federation runtime
  const federation = (window as any).__federation__;
  if (!federation?.import) {
    throw new Error("Module federation runtime not found on window.__federation__");
  }

  const module = await federation.import(`${remoteName}${normalizedExposed}`);
  if (!module?.default) {
    throw new Error("Remote module does not have a default export");
  }

  return module.default;
}

/**
 * Preload a microfrontend bundle
 */
export function preloadMicrofrontend(bundleUrl: string): void {
  const link = document.createElement("link");
  link.rel = "modulepreload";
  link.href = bundleUrl;
  document.head.appendChild(link);
}
